import React, { useContext, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LocalizeContext, LocalizeContextProps, Translate } from 'react-localize-redux';
import MultiChart from './MultiChart';
import {
  AppointmentOutcomeStatsResponse,
  buildOutcomeChartSeries,
} from '../utils/appointmentOutcomeStats';

interface AppointmentOutcomeSectionProps {
  data: AppointmentOutcomeStatsResponse;
  title?: string;
}

const MetricCard = ({ label, value }: { label: React.ReactNode; value: string | number }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
  </Paper>
);

const AppointmentOutcomeSection = ({ data, title }: AppointmentOutcomeSectionProps) => {
  const stats = data?.stats;
  const totals = stats?.totals;
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from
  // the package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const chartSeries = useMemo(() => {
    if (!stats) {
      return [];
    }
    return buildOutcomeChartSeries(stats, (key) => translate(key) as string);
  }, [stats, translate]);

  if (!stats || !totals) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">
          {title || <Translate id="hospital.analytics.appointment_outcomes.section_title" />}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.appointment_outcomes.section_subtitle" />
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.appointment_outcomes.scheduled" />}
            value={totals.scheduled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.appointment_outcomes.honored" />}
            value={totals.honored}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.appointment_outcomes.noShow" />}
            value={totals.noShow}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.appointment_outcomes.cancelledByPatient" />}
            value={totals.cancelledByPatient}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.appointment_outcomes.cancelledByHospital" />}
            value={totals.cancelledByHospital}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <MetricCard
            label={<Translate id="hospital.analytics.appointment_outcomes.noShowRate" />}
            value={`${totals.noShowRate}%`}
          />
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><Translate id="hospital.analytics.appointment_outcomes.service" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.appointment_outcomes.scheduled" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.appointment_outcomes.honored" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.appointment_outcomes.noShow" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.appointment_outcomes.cancelledByPatient" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.appointment_outcomes.cancelledByHospital" /></TableCell>
              <TableCell align="right"><Translate id="hospital.analytics.appointment_outcomes.noShowRate" /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.byDepartment.map((row) => (
              <TableRow key={row.departmentUuid}>
                <TableCell>{row.departmentName}</TableCell>
                <TableCell align="right">{row.scheduled}</TableCell>
                <TableCell align="right">{row.honored}</TableCell>
                <TableCell align="right">{row.noShow}</TableCell>
                <TableCell align="right">{row.cancelledByPatient}</TableCell>
                <TableCell align="right">{row.cancelledByHospital}</TableCell>
                <TableCell align="right">{row.noShowRate}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <MultiChart
        series={chartSeries}
        categories={stats.chart.categories}
        title={<Translate id="hospital.analytics.appointment_outcomes.chart_title" />}
        horizontal={false}
        type="bar"
        stacked
      />
    </Box>
  );
};

export default AppointmentOutcomeSection;
